import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { formatarTelefone } from "../utils/telefoneMask";
import { ChamadoFormData } from "../types/types";

interface UseChamadoFormParams {
  token: string | null;
  userEmail?: string;
  userId?: string | number;
  isTecnico?: boolean;
}

const INITIAL_STATE: ChamadoFormData = {
  empresa: "",
  descricao: "",
  num_chamado: "",
  tipo_solicitacao: "",
  telefone: "",
  email: "",
};

export const useChamadoForm = ({
  token,
  userEmail,
  userId,
  isTecnico = false,
}: UseChamadoFormParams) => {
  const [formData, setFormData] = useState<ChamadoFormData>({
    ...INITIAL_STATE,
    email: userEmail ?? "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.id]: e.target.value }));
  };

  const handleChangeTipoSolicitacao = (tipo_solicitacao: string) => {
    setFormData((prev) => ({ ...prev, tipo_solicitacao }));
  };

  const handleChangeTelefone = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      telefone: formatarTelefone(e.target.value),
    }));
  };

  const handleKeyDownTelefone = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { telefone } = formData;
    if (e.key !== "Backspace") return;

    if (telefone.length === 1) {
      setFormData((prev) => ({ ...prev, telefone: "" }));
      return;
    }
    if (telefone.length >= 10 && telefone.endsWith("-")) {
      setFormData((prev) => ({ ...prev, telefone: telefone.slice(0, -1) }));
    }
  };

  const resetForm = () =>
    setFormData({ ...INITIAL_STATE, email: userEmail ?? "" });

  const submitChamado = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API}/api/Chamado`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          uuid: "",
          empresa: formData.empresa,
          num_Chamado: formData.num_chamado,
          // Técnico pode informar o e-mail do cliente; cliente usa o próprio
          email: isTecnico ? formData.email : userEmail,
          telefone: formData.telefone,
          HORARIO_CRIADO: new Date().toISOString(),
          tipoSolicitacao: formData.tipo_solicitacao,
          descricao: formData.descricao,
          id_usuario: userId, // continua sendo o usuário logado (técnico ou cliente)
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(
          isTecnico
            ? "Chamado interno criado com sucesso!"
            : "Chamado criado com sucesso!",
        );
        resetForm();
        navigate("/chamados");
      } else {
        toast.error(data?.mensagem ?? "Não foi possível criar o chamado.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao enviar chamado. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    formData,
    isLoading,
    handleChange,
    handleChangeTipoSolicitacao,
    handleChangeTelefone,
    handleKeyDownTelefone,
    submitChamado,
  };
};
